/**
 * חילוץ הודעת שגיאה מתשובת Meta/Facebook Graph API.
 */
export function getFacebookError(resp: any): string {
  // 1) User-friendly messages
  const e = resp?.errorObj?.error;
  if (e) {
    if (e.error_user_title && e.error_user_msg) {
      return `${e.error_user_title}: ${e.error_user_msg}`;
    }
    // for example: "Invalid parameter: Invalid parameter: Invalid parameter"
    if (e.error_user_msg) {
      return e.error_user_msg;
    }
    if (e.error_user_title) {
      return e.error_user_title;
    }
    // Technical error message
    if (e.message && e.message !== 'Invalid parameter') {
      return e.message;
    }
  }

  // 3) Top level error message
  if (typeof resp.message === 'string' && resp.message.trim() && resp.message !== ':') {
    return resp.message;
  }

  // All other errors
  return 'Unexpected error. Please try again later.';
}

type ComponentLabel = 'BODY' | 'HEADER' | 'FOOTER' | 'BUTTONS' | string;

const componentLabelHe = (component: string): string => {
  const map: Record<string, string> = {
    BODY: 'גוף ההודעה',
    HEADER: 'כותרת',
    FOOTER: 'כותרת תחתונה',
    BUTTONS: 'כפתורים',
  };
  return map[component.toUpperCase()] || component;
};

/**
 * תרגום הודעות שגיאה נפוצות של Meta להגשת תבניות WhatsApp — לעברית ידידותית למשתמש.
 * אם אין התאמה ידועה, מחזיר ניסוח כללי בעברית עם פירוט טכני בסוגריים כשיש.
 */
export function translateFacebookErrorToHebrew(rawMessage: string | null | undefined): string {
  const msg = (rawMessage || '').trim();
  if (!msg) {
    return 'אירעה שגיאה לא צפויה. נסה שוב מאוחר יותר.';
  }

  const lower = msg.toLowerCase();

  // חסרים שדות ב-components / example ב-BODY/HEADER וכו'
  const missingExampleMatch = msg.match(
    /component of type\s+(\w+)\s+is missing expected field\(s\)\s*\(example\)/i,
  );
  if (
    missingExampleMatch ||
    (lower.includes('missing expected field') && lower.includes('example'))
  ) {
    const component = (missingExampleMatch?.[1] || 'BODY').toUpperCase() as ComponentLabel;
    return `ברכיב ${componentLabelHe(component)} (${component}) חסרה דוגמה למשתנים. יש למלא ערך דוגמה לכל משתנה בתבנית לפני ההגשה.`;
  }

  if (lower.includes('components') && lower.includes('missing expected field')) {
    return 'חסרים שדות נדרשים ברכיבי התבנית. בדוק שכל המשתנים כוללים דוגמה ושכל הרכיבים מולאו כראוי.';
  }

  if (lower.includes('invalid parameter')) {
    return 'פרמטר לא תקין בתבנית. בדוק את תוכן התבנית, המשתנים והדוגמאות ונסה שוב.';
  }

  if (lower.includes('already exists') || lower.includes('template name already')) {
    return 'תבנית בשם זה כבר קיימת. בחר שם אחר ונסה שוב.';
  }

  if (lower.includes('rate limit') || lower.includes('too many')) {
    return 'חריגה ממגבלת בקשות. המתן מעט ונסה שוב.';
  }

  if (lower.includes('permission') || lower.includes('not authorized') || lower.includes('oauth')) {
    return 'אין הרשאה לבצע פעולה זו בחשבון WhatsApp Business. בדוק את חיבור החשבון ונסה שוב.';
  }

  if (
    lower.includes('unexpected error') ||
    lower === 'unexpected error. please try again later.'
  ) {
    return 'אירעה שגיאה לא צפויה. נסה שוב מאוחר יותר.';
  }

  // ברירת מחדל: הודעה כללית + הטקסט המקורי לדיבאג
  return `שגיאה מהשרת של WhatsApp: ${msg}`;
}

/** חילוץ + תרגום לעברית במכה אחת */
export function getFacebookErrorHebrew(resp: any): string {
  return translateFacebookErrorToHebrew(getFacebookError(resp));
}
